import clsx from "clsx";
import {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ErrorMessage,
  getErrorMessageId,
} from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";

const variantClassMapping = {
  light: {
    label: "text-foreground-primary",
    container: "text-foreground-primary",
    placeholder: "placeholder:text-foreground-primary",
    // No border at rest (DS: flat filled box) - focus draws an offset ring
    // instead of swapping a border color, like FormField. An outline keeps the
    // gap transparent; a ring-offset would paint it white on the dark dialog.
    input:
      "text-foreground-primary focus:outline focus:outline-2 focus:outline-offset-4 focus:outline-foreground-primary",
    box: "h-8 w-7.5 rounded-md bg-surface-tertiary",
  },
  dark: {
    label: "text-foreground-primary-inverse",
    container: "text-foreground-primary-inverse",
    placeholder: "placeholder:text-foreground-primary-inverse",
    input:
      "text-foreground-primary-inverse focus:outline focus:outline-2 focus:outline-offset-4 focus:outline-foreground-primary-inverse",
    box: "h-8 w-7.5 rounded-md bg-surface-tertiary-inverse",
  },
};

export type TwoFactorConfirmationCodeForm = {
  code: string;
};

export const CONFIRMATION_CODE_LENGTH = 6;

export const TwoFactorConfirmationInput = ({
  error,
  clearError,
  variant = "light",
}: {
  error?: string;
  clearError?: () => void;
  variant?: "light" | "dark";
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const {
    setValue,
    formState: { errors },
    clearErrors,
    control,
  } = useFormContext<TwoFactorConfirmationCodeForm>();

  const hasError = !!errors.code || !!error;
  const code = useWatch({ control, name: "code" });

  // When code is reset to "", clear DOM input values
  useEffect(() => {
    if (code === "") {
      inputRefs.current.forEach((input) => (input.value = ""));
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [code]);

  // On backspace keydown, we want to delete to the previous input field
  // if the field is already empty.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && (e.target as HTMLInputElement).value === "") {
      const prevInput = inputRefs.current[Math.max(0, index - 1)];
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // On input, we want to move to the next input field if we get a character
  // and the field is not already holding a value.
  const handleInput = useCallback(
    (e: FormEvent<HTMLInputElement>, index: number) => {
      const inputElement = e.target as HTMLInputElement;
      const [firstCharacter = "", ...remainingInput] = inputElement.value;

      inputElement.value = firstCharacter;
      // Emptied boxes must shorten the code too, otherwise a stale digit is submitted.
      setValue("code", inputRefs.current.map((input) => input.value).join(""));

      if (!firstCharacter) {
        return;
      }

      clearErrors("code");

      if (clearError) {
        clearError();
      }

      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.value = remainingInput.join("");
        nextInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    [setValue, clearErrors, clearError],
  );

  // On paste, we want to paste the first 6 characters into the input fields
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasteData =
      e.clipboardData?.getData("text").slice(0, CONFIRMATION_CODE_LENGTH) ?? "";
    inputRefs.current.forEach((input, i) => {
      input.value = pasteData[i] || "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };

  const variantClasses = variantClassMapping[variant];
  const labelId = useId();

  return (
    <fieldset>
      {/* A <label> would only name the first of the digit inputs, so the
          visible text names the group and each input gets its own label. */}
      <div
        className={clsx(
          "flex flex-col gap-4 text-body-lg font-semibold leading-md",
          variantClasses.label,
        )}
        role="group"
        aria-labelledby={labelId}
      >
        {/* Like the former <label>: clicking the text focuses the first digit. */}
        <span id={labelId} onClick={() => inputRefs.current[0]?.focus()}>
          {t("portal:authenticator.sms.verification.label")}
        </span>
        <div
          className={clsx(
            "flex w-fit justify-center gap-1",
            variantClasses.container,
          )}
        >
          {Array.from({ length: CONFIRMATION_CODE_LENGTH }).map((_, index) => (
            <input
              key={index}
              className={clsx(
                "text-center text-body-base font-semibold leading-md",
                // Centred, the caret of an empty box sits on the "_"; right-
                // aligned with padding, the "_" stays centred and the caret
                // follows it like typed text.
                "focus:placeholder-shown:pr-2.5 focus:placeholder-shown:text-right",
                variantClasses.box,
                variantClasses.placeholder,
                hasError &&
                  "border-2 border-border-error text-foreground-error focus:outline focus:outline-2 focus:outline-offset-4 focus:outline-border-error",
                !hasError && variantClasses.input,
              )}
              aria-label={t("portal:authenticator.sms.verification.digit", {
                index: index + 1,
                count: CONFIRMATION_CODE_LENGTH,
              })}
              required
              aria-invalid={hasError ? "true" : "false"}
              aria-describedby={
                hasError ? getErrorMessageId("code") : undefined
              }
              maxLength={1}
              // DS code input: an empty box shows "_" in the text colour.
              placeholder="_"
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => handleKeyDown(e, index)}
              onInput={(e) => handleInput(e, index)}
              onPaste={handlePaste}
              ref={(el) => {
                if (el) {
                  inputRefs.current[index] = el;
                }
              }}
            />
          ))}
        </div>
      </div>
      {hasError && (
        <ErrorMessage name="code" message={error || errors.code?.message} />
      )}
    </fieldset>
  );
};
